import { calcAPCA } from 'apca-w3'

import { useSiteQuery } from '../../global/blocs/useSite'

type HexColor = `#${string}`

/**
 *
 * @param color a color in hex format
 * @param transparency accepts either a number between 0 and 255 or a string between 0% and 100%
 */
const getTransparentColor = (
  color: HexColor,
  transparency: number | string
) => {
  let t
  if (typeof transparency === 'string') {
    if (transparency.endsWith('%')) {
      t = +transparency.slice(0, -1)
    } else {
      t = +transparency
    }
    t *= 2.55
  } else {
    t = transparency
  }
  t = Math.min(255, Math.max(0, Math.round(t)))

  return `${color}${Number(t).toString(16).padStart(2, '0')}` as HexColor
}

const colorsMap = new Map<string, WebinarColor>()

class WebinarColor {
  /** The webinar color in a string format */
  color: HexColor

  /** The color expected to be behind this one.
   * (Used for calculating contrast ratios of transparent colors and for biasing contrast colors)
   */
  base: HexColor

  /**
   * A color contrasting with the current one that can be used on titles,
   * keywords, buttons and other interactive elements. It is biased against the base color.
   */
  contrast!: WebinarColor

  /**
   * A higher contrasting color that can be used for text and other places where extensive reading is needed.
   * In mast cases, it will be the same color as contrast.
   */
  highContrast!: WebinarColor

  /**
   * Used to get a transparent version of this color.
   * Accepts either a number between 0 and 255 or a string between 0% and 100%.
   */
  transparent!: (amount: number | string) => string;

  [Symbol.toPrimitive](hint: string) {
    switch (hint) {
      case 'string':
      case 'default':
        return this.color
      case 'number':
        return NaN
      default:
        return null
    }
  }

  get key() {
    return this.toKey()
  }

  toKey(color = this.color, base = this.base) {
    return `${color}-${base}`.toUpperCase()
  }

  // get transparencyBlend() {
  //   return alphaBlend(colorParsley(colorTEXT), colorParsley(colorBG))
  // }

  constructor(color: `#${string}`, base: `#${string}` = '#FFFFFF') {
    this.base = base
    this.color = color

    if (colorsMap.has(this.key)) {
      // eslint-disable-next-line no-constructor-return
      return colorsMap.get(this.key)!
    }
    colorsMap.set(this.key, this)

    const bestHighContrast =
      Math.abs(calcAPCA('#FFFFFF', color) as number) >
      Math.abs(calcAPCA('#000000', color) as number)
        ? '#FFFFFF'
        : '#000000'

    this.highContrast = new WebinarColor(bestHighContrast, this.color)

    const favorLight =
      Math.abs(calcAPCA('#FFFFFF', base) as number) >
      Math.abs(calcAPCA('#000000', base) as number)

    if (
      favorLight &&
      Math.abs(calcAPCA('#FFFFFF', this.color) as number) > 40
    ) {
      this.contrast = new WebinarColor('#FFFFFF', this.color)
    } else if (
      !favorLight &&
      Math.abs(calcAPCA('#000000', this.color) as number) > 40
    ) {
      this.contrast = new WebinarColor('#000000', this.color)
    } else {
      this.contrast = this.highContrast
    }

    // TODO: make it recursive as well
    // this.transparent = (amount) =>
    //   new WebinarColor(getTransparentColor(color, amount), this.color)

    this.transparent = (amount) => getTransparentColor(color, amount)
  }

  toString() {
    return this.color
  }
}

const useWebinarColors = () => {
  const { data: site } = useSiteQuery()
  const accentColor = site?.presentation_webinarRoomAccentColor || '#1EC95B'
  const sidebarColor =
    site?.presentation_webinarRoomSidebarBackgroundColor || '#282A29'
  const tabsColor =
    site?.presentation_webinarRoomTabBackgroundColor || '#323433'

  const sidebar = new WebinarColor(sidebarColor, '#000000')
  const accent = new WebinarColor(accentColor, sidebarColor)
  const tabs = new WebinarColor(tabsColor, sidebarColor)

  return {
    accent,
    sidebar,
    tabs,
  }
}

export { useWebinarColors }
